import React, {
  forwardRef,
  HTMLInputTypeAttribute,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { InputText } from 'primereact/inputtext';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import styles from './InputTextField.module.scss';

interface InputTextFieldProps {
  id: string;
  label?: string;
  name: string;
  value: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  help?: string;
  type?: HTMLInputTypeAttribute;
  className?: string;
  inputClassName?: string;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  children?: ReactNode;
  iconLabel?: IconProp;
  longErrorMessage?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const InputTextField = forwardRef<HTMLInputElement, InputTextFieldProps>(
  (
    {
      id,
      label,
      name,
      value,
      placeholder,
      onChange,
      error,
      help,
      type = 'text',
      className,
      readOnly,
      disabled,
      iconLabel,
      children,
      required,
      longErrorMessage,
      inputClassName,
      onKeyDown,
    },
    ref
  ) => {
    const errorMessageRef = useRef<HTMLParagraphElement>(null);
    const [errorOffset, setErrorOffset] = useState<number>(0);

    useLayoutEffect(() => {
      const updateErrorOffset = () => {
        if (errorMessageRef.current) {
          const height = errorMessageRef.current.clientHeight + 4;
          setErrorOffset(height);
        }
      };

      updateErrorOffset();

      window.addEventListener('resize', updateErrorOffset);

      return () => window.removeEventListener('resize', updateErrorOffset);
    }, [error]);

    return (
      <div className={classNames(styles.fieldContainer, className)}>
        <label
          htmlFor={id}
          className={classNames(styles.label, error && styles.errorLabel)}
        >
          {iconLabel && <FontAwesomeIcon icon={iconLabel} />}{' '}
          <span>
            {label}
            {required && '*'}
          </span>
        </label>
        <div className={classNames(children && styles.innerInputDiv)}>
          <InputText
            id={id}
            onKeyDown={onKeyDown}
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            className={classNames(
              styles.field,
              error && styles.invalid,
              inputClassName
            )}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            ref={ref}
          />
          {children}
          {error ? (
            <>
              <span className={styles.errorRut} />
              <p
                ref={errorMessageRef}
                style={longErrorMessage ? { bottom: `-${errorOffset}px` } : {}}
                className={classNames(
                  styles.errorMessage,
                  longErrorMessage && styles.longErrorMessage
                )}
              >
                {error}
              </p>
            </>
          ) : null}
          {help ? <p className={styles.help}>{help}</p> : null}
        </div>
      </div>
    );
  }
);

InputTextField.displayName = 'InputTextField';

export default InputTextField;
